import React, { useState } from "react";
import { SponsoredGuildCompetition } from "@devour/client";
import { useAsset } from "@/hooks/useAsset";
import AssetImage from "@/components/AssetImage";
import { DateTime } from "luxon";
import { FiAward, FiTrendingUp, FiUsers, FiChevronUp, FiChevronDown, FiGift } from "react-icons/fi";
import { IoTrophyOutline } from "react-icons/io5";
import { RxCaretRight } from "react-icons/rx";
import classNames from "classnames";
import "./GuildSponsorshipBanner.scss";

interface GuildSponsorshipBannerProps {
    competition?: SponsoredGuildCompetition | null;
    onViewRules?: () => void;
    onViewPrizes?: () => void;
}

export default function GuildSponsorshipBanner({
    competition,
    onViewRules,
    onViewPrizes,
}: GuildSponsorshipBannerProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    // Get sponsor logo if available - must be before any returns
    const { data: sponsorLogo } = useAsset(competition?.sponsorLogo);

    // Early return if no competition
    if (!competition) {
        return null;
    }

    // Get month name
    const monthName = DateTime
        .now()
        .setZone("America/New_York")
        .set({ month: competition.runningMonth })
        .monthLong;

    // Parse rewards by criteria
    const rewards = {
        winningGuild: competition.rewards.find(r => r.winningCriteria === "WINNING_GUILD"),
        guildLeader: competition.rewards.find(r => r.winningCriteria === "TOP_GUILD_XP_GAINER"),
        overallLeader: competition.rewards.find(r => r.winningCriteria === "TOP_OVERALL_XP_GAINER"),
    };

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={classNames("guild-sponsorship-banner", { expanded: isExpanded })}>
            {/* Collapsible header */}
            <div className="guild-sponsorship-banner__header" onClick={handleToggle}>
                <div className="header-content">
                    <div className="header-left">
                        <IoTrophyOutline className="trophy-icon" />
                        <span className="competition-name mobile-only">{monthName}</span>
                        <span className="competition-name desktop-only">{competition.name || `${monthName} Competition`}</span>
                        {competition.sponsorBrandName &&
              <>
                  <span className="separator">-</span>
                  <span className="sponsor-label desktop-only">Sponsored by:</span>
                  <span className="sponsor-name">{competition.sponsorBrandName}</span>
                  {competition.brandUrl &&
                  <a
                      href={competition.brandUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="visit-link mobile-only"
                      onClick={(e) => e.stopPropagation()}
                  >
                      <RxCaretRight />
                  </a>
                  }
              </>
                        }
                    </div>
                    <div className="header-right">
                        {competition.brandUrl &&
              <a
                  href={competition.brandUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="visit-link desktop-only"
                  onClick={(e) => e.stopPropagation()}
              >
                  <span className="visit-text">Visit</span>
                  <RxCaretRight />
              </a>
                        }
                        {isExpanded ? <FiChevronUp className="expand-arrow" /> : <FiChevronDown className="expand-arrow" />}
                    </div>
                </div>
            </div>

            {/* Collapsible content */}
            {isExpanded &&
        <div className="guild-sponsorship-banner__content">
            {/* Competition image */}
            {competition.promotionImage &&
            <div className="guild-sponsorship-banner__image">
                <AssetImage
                    assetId={competition.promotionImage}
                    alt={`${monthName} Competition`}
                    className="competition-image"
                />
            </div>
            }

            {/* Competition description */}
            <div className="guild-sponsorship-banner__description">
                <p>{competition.description || "Compete with your guild to earn exclusive rewards and climb the leaderboard!"}</p>
            </div>

            {/* Prize cards */}
            <div className="guild-sponsorship-banner__prizes">
                <div className="prize-pool-header">
                    <FiGift className="gift-icon" />
                    <span className="prize-pool-label">Prize Pool</span>
                </div>
                <div className="prizes-grid">
                    {rewards.winningGuild &&
                <div className="prize-card">
                    <div className="prize-icon">
                        <FiUsers />
                    </div>
                    <div className="prize-content">
                        <span className="prize-label">WINNING GUILD</span>
                        <span className="prize-value">{rewards.winningGuild.rewardConfig.name}</span>
                    </div>
                </div>
                    }

                    {rewards.guildLeader &&
                <div className="prize-card">
                    <div className="prize-icon">
                        <FiTrendingUp />
                    </div>
                    <div className="prize-content">
                        <span className="prize-label">GUILD XP LEADER</span>
                        <span className="prize-value">{rewards.guildLeader.rewardConfig.name}</span>
                    </div>
                </div>
                    }

                    {rewards.overallLeader &&
                <div className="prize-card">
                    <div className="prize-icon">
                        <FiAward />
                    </div>
                    <div className="prize-content">
                        <span className="prize-label">OVERALL XP LEADER</span>
                        <span className="prize-value">{rewards.overallLeader.rewardConfig.name}</span>
                    </div>
                </div>
                    }
                </div>
            </div>
        </div>
            }
        </div>
    );
}